import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AdminContentPanel, AdminPageShell } from '../../../components/common/AdminPageLayout/AdminPageLayout';
import PageHeader from '../../../components/common/PageHeader/PageHeader';
import Checkbox from '../../../components/common/Checkbox/Checkbox';
import PortalRoleSelect from '../../../components/common/PortalRoleSelect/PortalRoleSelect';
import Button from '../../../components/common/Button/Button';
import AlertBanner from '../../../components/common/AlertBanner/AlertBanner';
import Loading from '../../../components/common/Loading/Loading';
import {
  PortalUserProfileCredentialsSection,
  PortalUserProfileLayout,
  PortalUserProfileLeftHeader,
  PortalUserProfileMetaSection,
  PortalUserProfilePhotoSection,
  type PortalUserCredentialsErrors,
  type PortalUserCredentialsField,
  type PortalUserCredentialsValues,
} from '../../../components/common/PortalUserProfileForm';
import profileStyles from '../../../components/common/PortalUserProfileForm/PortalUserProfileForm.module.css';
import { PortalUserService } from '../../../services/PortalUserService';
import type { PortalRole } from '../../../types/portalUser';
import { resolvePortalUserRoles } from '../../../utils/permissions';
import styles from './UserRegister.module.css';

type UsersFormErrors = PortalUserCredentialsErrors & {
  roles?: string;
};

type UsersFormState = {
  credentials: PortalUserCredentialsValues;
  roles: PortalRole[];
  isActive: boolean;
};

const INITIAL_CREDENTIALS: PortalUserCredentialsValues = {
  fullName: '',
  email: '',
  currentPassword: '',
  password: '',
  confirmPassword: '',
};

const INITIAL_FORM_STATE: UsersFormState = {
  credentials: INITIAL_CREDENTIALS,
  roles: [],
  isActive: true,
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PASSWORD_MIN_LENGTH = 6;
const PASSWORD_MAX_LENGTH = 100;

const UserRegister: React.FC = () => {
  const navigate = useNavigate();
  const { userId } = useParams<{ userId: string }>();
  const isEditMode = Boolean(userId);
  const [form, setForm] = useState<UsersFormState>(INITIAL_FORM_STATE);
  const [initialForm, setInitialForm] = useState<UsersFormState>(INITIAL_FORM_STATE);
  const [errors, setErrors] = useState<UsersFormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const subtitle = useMemo(
    () => (isEditMode ? 'Edite os dados de acesso do portal' : 'Cadastro de acessos do portal'),
    [isEditMode],
  );

  useEffect(() => {
    if (!isEditMode || !userId) {
      setForm(INITIAL_FORM_STATE);
      setInitialForm(INITIAL_FORM_STATE);
      return;
    }

    let isMounted = true;

    const loadUser = async () => {
      setLoading(true);
      setSubmitError('');

      try {
        const user = await PortalUserService.getByAdmin(userId);
        if (!isMounted) return;

        const hydratedForm: UsersFormState = {
          credentials: {
            fullName: user.full_name,
            email: user.email,
            currentPassword: '',
            password: '',
            confirmPassword: '',
          },
          roles: resolvePortalUserRoles(user),
          isActive: user.is_active,
        };

        setForm(hydratedForm);
        setInitialForm(hydratedForm);
      } catch (error) {
        if (!isMounted) return;
        const message = error instanceof Error ? error.message : 'Não foi possível carregar o usuário.';
        setSubmitError(message);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadUser();

    return () => {
      isMounted = false;
    };
  }, [isEditMode, userId]);

  const validate = (): boolean => {
    const nextErrors: UsersFormErrors = {};
    const normalizedPassword = form.credentials.password.trim();
    const normalizedConfirmPassword = form.credentials.confirmPassword.trim();
    const shouldValidatePassword = !isEditMode || Boolean(normalizedPassword || normalizedConfirmPassword);

    if (!form.credentials.fullName.trim()) {
      nextErrors.fullName = 'Nome completo é obrigatório.';
    }

    if (!form.credentials.email.trim()) {
      nextErrors.email = 'E-mail é obrigatório.';
    } else if (!EMAIL_REGEX.test(form.credentials.email.trim())) {
      nextErrors.email = 'Informe um e-mail válido.';
    }

    if (shouldValidatePassword) {
      if (!normalizedPassword) {
        nextErrors.password = 'Senha é obrigatória.';
      } else if (normalizedPassword.length < PASSWORD_MIN_LENGTH) {
        nextErrors.password = `A senha deve ter pelo menos ${PASSWORD_MIN_LENGTH} caracteres.`;
      } else if (normalizedPassword.length > PASSWORD_MAX_LENGTH) {
        nextErrors.password = `A senha deve ter no máximo ${PASSWORD_MAX_LENGTH} caracteres.`;
      }

      if (!normalizedConfirmPassword) {
        nextErrors.confirmPassword = 'Confirmar senha é obrigatório.';
      } else if (normalizedPassword !== normalizedConfirmPassword) {
        nextErrors.confirmPassword = 'As senhas precisam ser iguais.';
      }
    }

    if ((form.roles ?? []).length === 0) {
      nextErrors.roles = 'Selecione ao menos um perfil.';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleCredentialsChange = (field: PortalUserCredentialsField, value: string) => {
    setForm((prev) => ({
      ...prev,
      credentials: {
        ...prev.credentials,
        [field]: value,
      },
    }));

    setErrors((prev) => ({
      ...prev,
      [field]: undefined,
    }));
  };

  const handleRolesChange = (roles: PortalRole[]) => {
    setForm((prev) => ({
      ...prev,
      roles,
    }));

    setErrors((prev) => ({
      ...prev,
      roles: undefined,
    }));
  };

  const handleReset = () => {
    setForm(isEditMode ? initialForm : INITIAL_FORM_STATE);
    setErrors({});
    setSubmitError('');
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    setSubmitError('');

    if (!validate()) {
      return;
    }

    setSubmitting(true);

    try {
      const successMessage = isEditMode && userId
        ? await PortalUserService.updateByAdmin({
          userId,
          fullName: form.credentials.fullName.trim(),
          email: form.credentials.email.trim().toLowerCase(),
          password: form.credentials.password.trim() || undefined,
          roles: form.roles ?? [],
          isActive: form.isActive,
        })
        : await PortalUserService.registerByAdmin({
          fullName: form.credentials.fullName.trim(),
          email: form.credentials.email.trim().toLowerCase(),
          password: form.credentials.password,
          roles: form.roles ?? [],
          isActive: form.isActive,
        });

      navigate('/administracao', {
        state: {
          usersSuccessMessage: successMessage,
        },
      });
    } catch (error) {
      const message = error instanceof Error
        ? error.message
        : (isEditMode ? 'Não foi possível atualizar a usuária.' : 'Não foi possível cadastrar a usuária.');
      setSubmitError(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <AdminPageShell>
        <AdminContentPanel>
          <Loading />
        </AdminContentPanel>
      </AdminPageShell>
    );
  }

  return (
    <AdminPageShell>
      <div className={styles.root}>
        <PageHeader title={isEditMode ? 'Editar usuário' : 'Cadastrar usuário'} subtitle={subtitle}>
          <Button
            variant="outline"
            size="small"
            onClick={() => navigate('/administracao')}
            disabled={submitting || loading}
          >
            Voltar
          </Button>
        </PageHeader>

        {submitError && <AlertBanner variant="error" message={submitError} className={styles.alert} />}

        <AdminContentPanel>
          <PortalUserProfileLayout
            withContentContainer={false}
            onSubmit={handleSubmit}
            leftColumn={(
              <>
                <PortalUserProfileLeftHeader
                  subtitle={isEditMode
                    ? 'Foto, perfis e status da usuária no portal'
                    : 'Defina os perfis e o status de acesso da usuária'}
                />
                {isEditMode && (
                  <PortalUserProfilePhotoSection userId={userId} disabled={submitting || loading} />
                )}
                <PortalRoleSelect
                  id="portal-user-roles"
                  label="Perfis"
                  required
                  values={form.roles ?? []}
                  onChange={handleRolesChange}
                  error={errors.roles}
                  disabled={submitting || loading}
                />
                <PortalUserProfileMetaSection label="Ativo">
                  <Checkbox
                    checked={form.isActive}
                    onPress={() => {
                      setForm((prev) => ({ ...prev, isActive: !prev.isActive }));
                    }}
                    label={form.isActive ? 'Sim' : 'Não'}
                    disabled={submitting || loading}
                    className={profileStyles.activeCheckbox}
                  />
                </PortalUserProfileMetaSection>
              </>
            )}
            rightColumn={(
              <PortalUserProfileCredentialsSection
                idPrefix="portal-user"
                variant={isEditMode ? 'admin-edit' : 'admin-create'}
                values={form.credentials}
                errors={errors}
                disabled={submitting || loading}
                onChange={handleCredentialsChange}
              />
            )}
            actions={(
              <>
                <Button
                  type="button"
                  variant="outline"
                  size="small"
                  onClick={handleReset}
                  disabled={submitting || loading}
                >
                  Limpar
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  size="small"
                  disabled={submitting || loading}
                >
                  {submitting
                    ? (isEditMode ? 'Salvando...' : 'Cadastrando...')
                    : (isEditMode ? 'Salvar alterações' : 'Cadastrar usuário')}
                </Button>
              </>
            )}
          />
        </AdminContentPanel>
      </div>
    </AdminPageShell>
  );
};

export default UserRegister;
